import { useParams, Link } from "react-router-dom";
import { Phone, Clock, Shield, CheckCircle, MapPin, ChevronRight, ChevronDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import SEOHead from "../components/SEOHead";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingCTA from "../components/FloatingCTA";
import ContactForm from "../components/ContactForm";
import { services } from "../data/services";
import { cities, getCityBySlug, generateCityContent } from "../data/cities";

export const CityPage = () => {
  const { citySlug } = useParams();
  // The citySlug parameter contains the city name (e.g., "melun")
  const city = getCityBySlug(citySlug);

  if (!city) {
    return (
      <>
        <Header />
        <main className="section-padding bg-white">
          <div className="container-custom text-center">
            <h1 className="text-4xl font-bold mb-4">Ville non trouvée</h1>
            <p className="text-zinc-600 mb-8">La ville que vous recherchez n'existe pas dans notre zone d'intervention.</p>
            <Link to="/zones-intervention">
              <Button className="bg-yellow-400 hover:bg-yellow-500 text-black">
                Voir toutes les villes
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const content = generateCityContent(city);

  // Get nearby cities (same department, excluding current)
  const nearbyCities = cities
    .filter(c => c.department === city.department && c.slug !== city.slug)
    .slice(0, 8);

  return (
    <>
      <SEOHead
        title={content.title}
        description={content.metaDescription}
        type="LocalBusiness"
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 bg-zinc-950 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80"
              alt={`Serrurier ${city.name} - Intervention serrurerie`}
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/95 to-zinc-950/80" />
          </div>

          <div className="container-custom relative z-10">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-zinc-400 mb-8">
              <Link to="/" className="hover:text-yellow-400 transition-colors">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/zones-intervention" className="hover:text-yellow-400 transition-colors">Zones</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-yellow-400">{city.name}</span>
            </nav>

            <div className="max-w-3xl">
              {/* Location badge */}
              <div className="inline-flex items-center gap-2 bg-yellow-400/10 border border-yellow-400/30 rounded-sm px-4 py-2 mb-6">
                <MapPin className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-400 text-sm font-medium">
                  {city.postalCode} – {city.department}
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-bold uppercase tracking-tight text-white mb-6 font-heading" data-testid="city-page-title">
                {content.h1}
              </h1>
              <p className="text-lg text-zinc-300 mb-8">
                {content.intro}
              </p>

              {/* Trust badges */}
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-zinc-900/50 border border-zinc-800 rounded-sm px-4 py-2">
                  <Clock className="w-5 h-5 text-yellow-400" />
                  <span className="text-white text-sm">Intervention 15-30 min</span>
                </div>
                <div className="flex items-center gap-2 bg-zinc-900/50 border border-zinc-800 rounded-sm px-4 py-2">
                  <Shield className="w-5 h-5 text-yellow-400" />
                  <span className="text-white text-sm">Disponible 24h/24</span>
                </div>
              </div>

              {/* CTA */}
              <a href="tel:0668032561" data-testid="city-call-button">
                <Button className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-wide px-8 py-6 text-lg rounded-sm animate-pulse-glow">
                  <Phone className="w-5 h-5 mr-3" />
                  Appeler – 06 68 03 25 61
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Content Sections */}
        <section className="section-padding bg-white">
          <div className="container-custom">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-12">
                {content.sections.map((section, index) => (
                  <div key={index} className="prose prose-lg max-w-none">
                    <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-zinc-900 mb-4 font-heading">
                      {section.h2}
                    </h2>
                    <p className="text-zinc-600 leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                ))}

                {/* Services list */}
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-8">
                  <h2 className="text-2xl font-bold uppercase tracking-tight text-zinc-900 mb-6 font-heading">
                    Nos services à {city.name}
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {services.map((service) => (
                      <Link
                        key={service.id}
                        to={`/service/${service.slug}`}
                        className="flex items-center gap-3 p-4 bg-white border border-zinc-200 rounded-sm hover:border-yellow-400 hover:bg-yellow-50 transition-all group"
                      >
                        <CheckCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                        <span className="text-zinc-700 group-hover:text-zinc-900 font-medium">
                          {service.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* FAQ Section */}
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-tight text-zinc-900 mb-6 font-heading">
                    Questions fréquentes
                  </h2>
                  <Accordion type="single" collapsible className="space-y-4">
                    {content.faq.map((faq, index) => (
                      <AccordionItem 
                        key={index} 
                        value={`faq-${index}`}
                        className="border border-zinc-200 rounded-sm px-6 bg-white"
                      >
                        <AccordionTrigger className="text-left font-bold text-zinc-900 hover:text-yellow-500">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-zinc-600">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>

                {/* CTA Box */}
                <div className="bg-zinc-950 rounded-sm p-8 text-center">
                  <h3 className="text-2xl font-bold uppercase text-white mb-4 font-heading">
                    Besoin d'un serrurier à {city.name} ?
                  </h3>
                  <p className="text-zinc-300 mb-6">
                    Notre équipe intervient rapidement pour tous vos besoins en serrurerie.
                  </p>
                  <a href="tel:0668032561">
                    <Button className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-wide px-8 py-4">
                      <Phone className="w-5 h-5 mr-2" />
                      06 68 03 25 61
                    </Button>
                  </a>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-8">
                {/* Contact Card */}
                <div className="bg-yellow-400 rounded-sm p-6 text-center sticky top-24">
                  <h3 className="text-xl font-bold uppercase tracking-tight text-black mb-2 font-heading">
                    Serrurier {city.name}
                  </h3>
                  <p className="text-black/80 mb-4 text-sm">
                    Intervention rapide 24h/24
                  </p>
                  <a href="tel:0668032561" className="block mb-6">
                    <div className="bg-black text-white font-bold text-xl py-4 rounded-sm hover:bg-zinc-800 transition-colors">
                      06 68 03 25 61
                    </div>
                  </a>

                  <div className="text-left space-y-2 text-sm text-black/80">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-black" />
                      Intervention 15-30 min
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-black" />
                      Devis avant intervention
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-black" />
                      Disponible 24h/24 7j/7
                    </div>
                  </div>
                </div>

                {/* Nearby Cities */}
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-6">
                  <h3 className="text-xl font-bold uppercase tracking-tight text-zinc-900 mb-6 font-heading">
                    Villes proches
                  </h3>
                  <div className="space-y-2">
                    {nearbyCities.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/serrurier/${c.slug}`}
                        className="flex items-center gap-2 p-2 hover:bg-white rounded-sm transition-colors group"
                      >
                        <MapPin className="w-4 h-4 text-yellow-500" />
                        <span className="text-zinc-700 group-hover:text-zinc-900 text-sm">
                          Serrurier {c.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                  <Link 
                    to="/zones-intervention"
                    className="block mt-4 text-yellow-500 font-medium text-sm hover:text-yellow-600 transition-colors"
                  >
                    Voir toutes les villes →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ContactForm />
      </main>
      <Footer />
      <FloatingCTA />
    </>
  );
};

export default CityPage;
